import { createTransportToWorker, MonacoLspClient } from "@vscode/monaco-lsp-client/src/index"
import * as monaco from "monaco-editor-core"
import {
  conf as javascriptConfiguration,
  language as javascriptLanguage,
} from "monaco-editor/languages/definitions/javascript/javascript.js"
import {
  conf as typescriptConfiguration,
  language as typescriptLanguage,
} from "monaco-editor/languages/definitions/typescript/typescript.js"
import { frameFileSystemUpdate, normalizeWorkspaceEditUris } from "./lsp-filesystem"
import { languageForFile } from "./editor-languages"
import { resolveSymlinks, type Symlinks } from "./symlinks"

const headerWords = 4
const readPosition = 0
const writePosition = 1
const closed = 2
const signal = 3
const bufferSize = 4 * 1024 * 1024

export type TsgoStatus = "mounting files" | "starting tsc.wasm" | "initializing LSP" | "ready"

type WorkerMessage = {
  type: "lsp" | "drain" | "status" | "stderr" | "error" | "filesystem"
  id?: number
  message?: any
  status?: TsgoStatus
  aliases?: Array<{ path: string; type: number }>
}

type StartTsgoLspOptions = {
  configFileName: string
  effectiveConfigText: string
  libraries: Record<string, string>
  models: readonly monaco.editor.ITextModel[]
  module: WebAssembly.Module
  onError(message: string): void
  shouldHandleDiagnostics(model: monaco.editor.ITextModel): boolean
  readPackageFile(fileName: string): string | undefined
  onPackageModel(model: monaco.editor.ITextModel): void
  onStatus(status: TsgoStatus, serverInfo?: string): void
  symlinks?: Symlinks
}

export type TsgoLspController = {
  updateEffectiveConfig(text: string): void
  refreshDiagnostics(): void
  updateFiles(files: ReadonlyMap<string, string>): Promise<void>
  readFile(fileName: string): Promise<string | undefined>
}

class RingBufferWorker {
  readonly #worker = new Worker(new URL("./tsgo-lsp.worker.js", import.meta.url), {
    type: "module",
  })
  readonly #state: Int32Array
  readonly #data: Uint8Array
  readonly #queue: Uint8Array[] = []
  readonly #listeners = new Map<EventListenerOrEventListenerObject, EventListener>()
  readonly #pendingRequests = new Map<string | number, string>()
  readonly #configFileName: string
  readonly #mountedFiles = new Map<string, string>()
  readonly #fileUpdates = new Map<number, { resolve(): void; reject(error: Error): void }>()
  #nextFileUpdateId = 0
  #lastFileUpdate = Promise.resolve()
  #failure: Error | undefined
  #configVersion = 1
  #effectiveConfigText: string
  #queueOffset = 0

  onStatus?: (status: TsgoStatus) => void
  onError?: (message: string) => void
  onServerInfo?: (serverInfo: string) => void

  constructor(stdin: SharedArrayBuffer, configFileName: string, effectiveConfigText: string) {
    this.#state = new Int32Array(stdin, 0, headerWords)
    this.#data = new Uint8Array(stdin, headerWords * Int32Array.BYTES_PER_ELEMENT)
    this.#configFileName = configFileName
    this.#effectiveConfigText = effectiveConfigText
    this.#worker.addEventListener("message", (event: MessageEvent<WorkerMessage>) => {
      if (event.data.type === "drain") {
        this.#flush()
      } else if (event.data.type === "status" && event.data.status) {
        this.onStatus?.(event.data.status)
      } else if (event.data.type === "error") {
        this.#failure = new Error(event.data.message ?? "Language-server worker failed")
        for (const pending of this.#fileUpdates.values()) pending.reject(this.#failure)
        this.#fileUpdates.clear()
        this.onError?.(event.data.message)
      } else if (event.data.type === "filesystem" && event.data.id !== undefined) {
        // A watcher on a symlink observes target changes under the link's path.
        if (event.data.aliases?.length) {
          this.postMessage({
            jsonrpc: "2.0",
            method: "workspace/didChangeWatchedFiles",
            params: {
              changes: event.data.aliases.map(({ path, type }) => ({ uri: monaco.Uri.file(path).toString(), type })),
            },
          })
        }
        this.#fileUpdates.get(event.data.id)?.resolve()
        this.#fileUpdates.delete(event.data.id)
      } else if (event.data.type === "stderr") {
        console.warn("[tsgo]", event.data.message)
      } else if (event.data.type === "lsp") {
        void this.#handleLspMessage(event.data.message)
      }
    })
    this.#worker.addEventListener("error", event => {
      const error = new Error(event.message || "Language-server worker failed")
      this.#failure = error
      for (const pending of this.#fileUpdates.values()) pending.reject(error)
      this.#fileUpdates.clear()
      this.onError?.(error.message)
    })
  }

  start(
    stdin: SharedArrayBuffer,
    module: WebAssembly.Module,
    libraries: Record<string, string>,
    files: Record<string, string>,
    symlinks: Symlinks
  ) {
    if (Object.keys(symlinks).length) {
      for (const [path, text] of Object.entries(files)) this.#mountedFiles.set(path, text)
    }
    this.#worker.postMessage({
      type: "init",
      stdin,
      libraries,
      module,
      files,
      symlinks,
    })
  }

  postMessage(message: unknown) {
    message = this.#rewriteConfigMessage(message)
    const lspMessage = message as { id?: string | number; method?: string }
    if (lspMessage.id !== undefined && lspMessage.method) {
      this.#pendingRequests.set(lspMessage.id, lspMessage.method)
    }

    this.#queue.push(this.#frameMessage(message))
    this.#flush()
  }

  #frameMessage(message: unknown) {
    const body = new TextEncoder().encode(JSON.stringify(message))
    const header = new TextEncoder().encode(`Content-Length: ${body.length}\r\n\r\n`)
    const framed = new Uint8Array(header.length + body.length)
    framed.set(header)
    framed.set(body, header.length)
    if (framed.length > this.#data.length) {
      throw new Error(`LSP message exceeds the ${this.#data.length}-byte stdin buffer`)
    }
    return framed
  }

  updateEffectiveConfig(text: string) {
    if (text === this.#effectiveConfigText) return false
    this.#effectiveConfigText = text
    this.postMessage({
      jsonrpc: "2.0",
      method: "textDocument/didChange",
      params: {
        contentChanges: [{ text }],
        textDocument: {
          uri: monaco.Uri.file(this.#configFileName).toString(),
          version: this.#configVersion + 1,
        },
      },
    })
    return true
  }

  updateFiles(files: ReadonlyMap<string, string>) {
    if (this.#failure) return Promise.reject(this.#failure)
    const changed: Record<string, string> = {}
    const deleted: string[] = []
    const changes: Array<{ uri: string; type: number }> = []
    for (const [path, text] of files) {
      if (this.#mountedFiles.get(path) === text) continue
      changed[path] = text
      changes.push({ uri: monaco.Uri.file(path).toString(), type: this.#mountedFiles.has(path) ? 2 : 1 })
    }
    for (const path of this.#mountedFiles.keys()) {
      if (files.has(path)) continue
      deleted.push(path)
      changes.push({ uri: monaco.Uri.file(path).toString(), type: 3 })
    }
    if (!changes.length) return this.#lastFileUpdate
    const id = ++this.#nextFileUpdateId
    const record = frameFileSystemUpdate({ id, files: changed, deleted })
    const notification = this.#frameMessage({
      jsonrpc: "2.0",
      method: "workspace/didChangeWatchedFiles",
      params: { changes },
    })
    const completed = new Promise<void>((resolve, reject) => this.#fileUpdates.set(id, { resolve, reject }))
    this.#lastFileUpdate = completed
    this.#mountedFiles.clear()
    for (const [path, text] of files) this.#mountedFiles.set(path, text)
    this.#queue.push(record, notification)
    this.#flush()
    return completed
  }

  addEventListener(type: string, listener: EventListenerOrEventListenerObject) {
    if (type !== "message") return
    const callback: EventListener = typeof listener === "function" ? listener : event => listener.handleEvent(event)
    this.#listeners.set(listener, callback)
  }

  removeEventListener(type: string, listener: EventListenerOrEventListenerObject) {
    if (type === "message") this.#listeners.delete(listener)
  }

  #rewriteConfigMessage(message: unknown) {
    if (!message || typeof message !== "object") return message
    const candidate = message as {
      method?: string
      params?: {
        contentChanges?: Array<{ text?: string }>
        textDocument?: { text?: string; uri?: string; version?: number }
      }
    }
    const document = candidate.params?.textDocument
    if (!document?.uri || monaco.Uri.parse(document.uri).path !== this.#configFileName) return message
    if (candidate.method === "textDocument/didOpen") {
      this.#configVersion = Math.max(this.#configVersion, document.version ?? this.#configVersion)
      return {
        ...candidate,
        params: {
          ...candidate.params,
          textDocument: {
            ...document,
            text: this.#effectiveConfigText,
          },
        },
      }
    }
    if (candidate.method === "textDocument/didChange") {
      this.#configVersion = Math.max(this.#configVersion + 1, document.version ?? 0)
      return {
        ...candidate,
        params: {
          ...candidate.params,
          contentChanges: [{ text: this.#effectiveConfigText }],
          textDocument: {
            ...document,
            version: this.#configVersion,
          },
        },
      }
    }
    return message
  }

  #flush() {
    while (this.#queue.length > 0) {
      const readPos = Atomics.load(this.#state, readPosition) >>> 0
      const writePos = Atomics.load(this.#state, writePosition) >>> 0
      const free = this.#data.length - ((writePos - readPos) >>> 0)
      if (free <= 0) return

      const current = this.#queue[0]
      const length = Math.min(free, current.length - this.#queueOffset)
      const start = writePos % this.#data.length
      const first = Math.min(length, this.#data.length - start)
      this.#data.set(current.subarray(this.#queueOffset, this.#queueOffset + first), start)
      if (first < length) {
        this.#data.set(current.subarray(this.#queueOffset + first, this.#queueOffset + length), 0)
      }
      this.#queueOffset += length
      Atomics.store(this.#state, writePosition, writePos + length)
      Atomics.add(this.#state, signal, 1)
      Atomics.notify(this.#state, signal)

      if (this.#queueOffset === current.length) {
        this.#queue.shift()
        this.#queueOffset = 0
      }
    }
  }

  async #handleLspMessage(message: any) {
    const method = message?.method
    const label = method ?? this.#pendingRequests.get(message?.id)
    if (method === "textDocument/publishDiagnostics") return
    if (method === "workspace/applyEdit" && message.params?.edit) {
      message.params.edit = normalizeWorkspaceEditUris(message.params.edit, normalizeLibraryUri)
    }
    if (label === "textDocument/rename") {
      message.result = normalizeWorkspaceEditUris(message.result, normalizeLibraryUri)
    } else if (label === "textDocument/codeAction") {
      message.result = message.result?.map((action: { edit?: unknown }) => ({
        ...action,
        ...(action.edit ? { edit: normalizeWorkspaceEditUris(action.edit, normalizeLibraryUri) } : {}),
      }))
    } else if (label === "codeAction/resolve" && message.result?.edit) {
      message.result.edit = normalizeWorkspaceEditUris(message.result.edit, normalizeLibraryUri)
    }
    if (
      label &&
      [
        "textDocument/definition",
        "textDocument/declaration",
        "textDocument/typeDefinition",
        "textDocument/implementation",
        "textDocument/references",
      ].includes(label)
    ) {
      message.result = normalizeLibraryLocations(message?.result)
      await ensureDefinitionModels(message.result)
    }
    if (message?.id !== undefined && !method) {
      this.#pendingRequests.delete(message.id)
    }
    if (message?.result?.capabilities) {
      const info = message.result.serverInfo
      if (info?.name) {
        const name = info.name === "typescript-go" ? "TypeScript" : info.name
        this.onServerInfo?.(`${name}${info.version ? ` ${info.version}` : ""}`)
      }
      this.onStatus?.("ready")
    }

    const forwarded = new MessageEvent("message", { data: message })
    for (const listener of this.#listeners.values()) listener(forwarded)
  }
}

let languageRegistered = false
let definitionFilesPromise: Promise<Record<string, string>> | undefined
let readPackageFile: StartTsgoLspOptions["readPackageFile"] | undefined
let onPackageModel: StartTsgoLspOptions["onPackageModel"] | undefined
let definitionSymlinks: Symlinks = {}

export function registerPlaygroundLanguages() {
  if (languageRegistered) return
  languageRegistered = true
  for (const [id, extensions] of [
    ["typescript", [".ts", ".mts", ".cts"]],
    ["typescriptreact", [".tsx"]],
    ["javascript", [".js", ".mjs", ".cjs"]],
    ["javascriptreact", [".jsx"]],
  ] as const) {
    const javascript = id.startsWith("javascript")
    monaco.languages.register({ id, extensions: [...extensions] })
    monaco.languages.setLanguageConfiguration(id, javascript ? javascriptConfiguration : typescriptConfiguration)
    monaco.languages.setMonarchTokensProvider(id, javascript ? javascriptLanguage : typescriptLanguage)
  }
  monaco.languages.register({ id: "json", extensions: [".json"] })
  monaco.languages.setLanguageConfiguration("json", {
    brackets: [
      ["{", "}"],
      ["[", "]"],
    ],
    comments: { lineComment: "//", blockComment: ["/*", "*/"] },
  })
  monaco.languages.setMonarchTokensProvider("json", {
    tokenizer: {
      root: [
        [/"(?:\\.|[^"\\])*"(?=\s*:)/, "string.key.json"],
        [/"(?:\\.|[^"\\])*"/, "string.value.json"],
        [/\b(?:true|false|null)\b/, "keyword.json"],
        [/-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/, "number"],
        [/[{}\[\],:]/, "delimiter"],
        [/\/\/.*$/, "comment"],
      ],
    },
  })
}

export function startTsgoLsp(options: StartTsgoLspOptions) {
  if (!crossOriginIsolated) {
    throw new Error("The TypeScript language server requires cross-origin isolation; reload once to activate it.")
  }

  readPackageFile = options.readPackageFile
  onPackageModel = options.onPackageModel
  definitionSymlinks = options.symlinks ?? {}
  definitionFilesPromise = Promise.resolve(
    Object.fromEntries(
      Object.entries(options.libraries).map(([fileName, text]) => [
        `/typescript/lib/${fileName.slice(fileName.lastIndexOf("/") + 1)}`,
        text,
      ])
    )
  )
  const stdin = new SharedArrayBuffer(headerWords * Int32Array.BYTES_PER_ELEMENT + bufferSize)
  const configModelPath = resolveSymlinks(options.configFileName, options.symlinks ?? {}) ?? options.configFileName
  const worker = new RingBufferWorker(stdin, configModelPath, options.effectiveConfigText)
  let serverInfo: string | undefined
  worker.onStatus = status => options.onStatus(status, serverInfo)
  worker.onServerInfo = info => {
    serverInfo = info
  }
  worker.onError = options.onError
  // Package files are mounted in batches; only project files are open LSP documents.
  worker.start(
    stdin,
    options.module,
    options.libraries,
    {
      ...Object.fromEntries(options.models.map(model => [model.uri.path, model.getValue()])),
      [resolveSymlinks(options.configFileName, options.symlinks ?? {}) ?? options.configFileName]:
        options.effectiveConfigText,
    },
    options.symlinks ?? {}
  )

  const transport = createTransportToWorker(worker as unknown as Worker)
  const client = new MonacoLspClient(transport, {
    shouldHandleDiagnostics: options.shouldHandleDiagnostics,
    shouldSynchronizeModel: options.shouldHandleDiagnostics,
  })
  return {
    updateEffectiveConfig(text: string) {
      if (worker.updateEffectiveConfig(text)) client.refreshDiagnostics()
    },
    refreshDiagnostics() {
      client.refreshDiagnostics()
    },
    updateFiles(files: ReadonlyMap<string, string>) {
      return worker.updateFiles(files)
    },
    async readFile(fileName: string) {
      const libraries = await getDefinitionFiles()
      return (
        readPackageFile?.(fileName) ??
        libraries[fileName] ??
        (/^\/lib(?:\.[\w.]+)?\.d\.ts$/.test(fileName) ? libraries[`/typescript/lib${fileName}`] : undefined)
      )
    },
  } satisfies TsgoLspController
}

function normalizeLibraryLocations(result: unknown): unknown {
  if (Array.isArray(result)) return result.map(normalizeLibraryLocations)
  if (!result || typeof result !== "object") return result

  const location = result as {
    uri?: string
    targetUri?: string
  }
  const uri = location.targetUri ?? location.uri
  if (!uri) return result
  const normalized = normalizeLibraryUri(uri)
  if (normalized === uri) return result
  return "targetUri" in location ? { ...location, targetUri: normalized } : { ...location, uri: normalized }
}

function normalizeLibraryUri(uri: string) {
  const parsed = monaco.Uri.parse(uri)
  if (parsed.scheme === "file") {
    const resolved = resolveSymlinks(parsed.path, definitionSymlinks)
    return resolved === undefined || resolved === parsed.path ? uri : parsed.with({ path: resolved }).toString()
  }
  if (parsed.scheme !== "bundled" || !/^\/libs\/lib(?:\..*)?\.d\.ts$/i.test(parsed.path)) {
    return uri
  }
  return monaco.Uri.file(`/typescript/lib/${parsed.path.slice("/libs/".length)}`).toString()
}

async function ensureDefinitionModels(result: unknown) {
  const locations = Array.isArray(result) ? result : [result]
  const uris = new Set<string>()
  for (const location of locations) {
    if (!location || typeof location !== "object") continue
    const candidate = location as { uri?: string; targetUri?: string }
    const uri = candidate.targetUri ?? candidate.uri
    if (uri && monaco.Uri.parse(uri).scheme === "file") uris.add(uri)
  }

  const definitionFiles = uris.size > 0 ? await getDefinitionFiles() : {}
  for (const uri of uris) {
    const monacoUri = monaco.Uri.parse(uri)
    if (monaco.editor.getModel(monacoUri)) continue
    const packageContents = readPackageFile?.(monacoUri.path)
    const contents = packageContents ?? definitionFiles[monacoUri.path]
    if (contents === undefined) continue
    const model = monaco.editor.createModel(contents, languageForFile(monacoUri.path), monacoUri)
    if (packageContents !== undefined) onPackageModel?.(model)
  }
}

function getDefinitionFiles() {
  if (!definitionFilesPromise) throw new Error("Language-server libraries are not initialized")
  return definitionFilesPromise
}

export { monaco }
